import { createApi, endpoint } from '@/shared/lib/http/endpoint';

import type { Prettify } from '@/shared/utils/types';

// Identity surface — "who is the current user". Three endpoints driving
// stores/auth-related fetches: account profile + user record + permission
// table. All are under the `/account` module prefix even when user-scoped.

// /account/account-info — account profile for the active account.
export type AccountInfoRequest = Api.PbAccount.GetAccountInfoReq;
export type AccountInfoResponse = Prettify<
  Api.PbAccount.Account & {
    remainingTime?: number;
    remindCode?: number;
    // Cloud-typings doesn't surface it; local patch per the audit pattern.
    isRootAccount?: boolean;
    // Paid-tier flag. Gates the Support page (paid ticket list vs free-tier
    // community cards). The account-info JSON is a hand-assembled map whose
    // business fields are camelCase (like every sibling above), and legacy
    // reads `accountInfo.isPaid` — so camelCase is the live contract. proto
    // Org.is_paid (snake_case) describes the proto, NOT this endpoint's JSON;
    // `is_paid` is kept only as a defensive fallback. P1 — pin with a
    // Pattern-6 contract row in celerdata-byoc-api-test.
    isPaid?: boolean;
    is_paid?: boolean;
    // Set once the account is linked to Zendesk; gates the Support dialogs
    // (Phase 2). Same camelCase-vs-snake caveat as isPaid.
    hasZendeskAccount?: boolean;
    has_zendesk_account?: boolean;
    // Account-settings (Advanced tab) gates. camelCase JSON fields not in the
    // proto; local patch like the fields above. allowDisableEditor gates the
    // Security card; enabledFeatures includes 'AI_ASSISTANT' to gate the AI
    // switch; oidcEnabled drives the MCP-enable dialog's OIDC warning.
    allowDisableEditor?: boolean;
    enabledFeatures?: string[];
    oidcEnabled?: boolean;
    // The free-tier check posts it. The camelCase field, not proto `org_id`, is
    // the live contract.
    orgId?: string;
  }
>;

// /account/user-info — current user record, keyed by bizID/ubid.
export type UserInfoRequest = { bizID: string };
export type UserInfoResponse = Prettify<
  Api.PbAccount.User & {
    perms?: string[];
    data_perms?: Record<string, string[]>;
    marketplaceCustomerId?: string;
    sourceFrom?: number;
    accountID?: string;
    userID?: string;
    accountEmail?: string;
    // Marketplace users are bound to the CSP they subscribed through; the
    // create-cluster flow preselects it and offers no other.
    cspId?: string;
  }
>;

// /account/user-perms — fresh perms fetch.  TODO(backend): proto GetUserPermsRes.
export type UserPermsResponse = {
  perms?: string[];
  dataPerms?: Record<string, string[]>;
};

export const identityApi = createApi({
  '/account/account-info': endpoint<AccountInfoRequest, AccountInfoResponse>('POST'),
  '/account/user-info': endpoint<UserInfoRequest, UserInfoResponse>('POST'),
  '/account/user-perms': endpoint<void, UserPermsResponse>('POST'),
});
